/**
 * Vincent (2026-07-22) — P1-4 Phase 3 refactor : hook encapsulant le
 * polling du statut pipeline `/api/videos/{id}/pipeline-status` sur
 * 3 s d'intervalle avec monotonic-progress-clamp (évite l'effet yoyo
 * quand la queue + le pipeline race pour update). Extrait de
 * VideoDetailPage.js (~35 lignes économisées).
 *
 * Contrat :
 *   const { pipelineStatus, startPolling, stopPolling } =
 *     usePipelineStatus({ videoId, api, onComplete });
 *
 * - `startPolling()` : lance un fetch immédiat + un interval de 3 s.
 * - `stopPolling()`  : coupe l'interval (usage rare — auto-cleanup au unmount).
 * - `onComplete()`   : callback appelé quand :
 *     - le status devient "complete", OU
 *     - Vincent B639 (Eric 2026-07-31 SPEC STRICK PROD) — le status
 *       devient "paused_pending_fusion" ou "paused_pending_extraction".
 *       Ces états signifient que Whisper + diarisation sont TERMINÉS et
 *       que les segments viennent d'être insérés en DB : le frontend DOIT
 *       recharger `loadVideoData()` pour que `data.segments` (utilisé par
 *       l'onglet Transcriptions) reflète la nouvelle transcription. Sans
 *       ce trigger, l'onglet Transcriptions restait vide indéfiniment
 *       après un rerun-whisper / reprocess / Recommencer transcription
 *       (config Diarisation=on + ID faciale=off notamment).
 * - Le polling s'arrête automatiquement dès que le status est
 *   "complete" ou "failed". Sur "paused_pending_*", le polling CONTINUE
 *   (le user peut reprendre le pipeline via un bouton, le stage change).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import useBrowserThrottle from "./useBrowserThrottle";  // Vincent B1019

const _PAUSED_AFTER_TRANSCRIPTION = new Set([
  "paused_pending_fusion",
  "paused_pending_extraction",
]);

const BASE_POLL_INTERVAL_MS = 3000;

export function usePipelineStatus({ videoId, api, onComplete }) {
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const pollingRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  // Vincent B1019 (Eric 2026-02-26 SPEC STRICK PROD) — Multiplicateur
  // d'intervalle appliqué quand le navigateur signale la saturation
  // (ERR_INSUFFICIENT_RESOURCES). Passe de 3s à 15s pendant 30s pour
  // laisser au pool sockets Chrome le temps de se libérer.
  const throttleMultiplier = useBrowserThrottle();
  const throttleMultiplierRef = useRef(throttleMultiplier);
  useEffect(() => { throttleMultiplierRef.current = throttleMultiplier; }, [throttleMultiplier]);
  // Vincent B1017 (Eric 2026-02-26 SPEC STRICK PROD) — AbortController
  // partagé pour couper toutes les requêtes en vol au unmount / stop.
  const abortRef = useRef(null);
  // Vincent B639 — anti-double-trigger : on ne rappelle `onComplete` qu'UNE
  // fois par entrée dans un état paused_pending_* (sinon le polling toutes
  // les 3 s referait `loadVideoData` en boucle tant que le user ne reprend
  // pas le pipeline).
  const pausedTriggeredRef = useRef(false);
  // Garder onComplete à jour sans re-lancer le polling
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const fetchPipelineStatus = useCallback(async () => {
    try {
      const { data: status } = await axios.get(
        `${api}/api/videos/${videoId}/pipeline-status`,
        {
          withCredentials: true,
          // Vincent B1015 (Eric 2026-02-26 SPEC STRICK PROD) — Poll toutes
          // les 3s : pas besoin de retry Cloudflare (le poll suivant fera
          // office de retry naturel). Limiter à 1 pour éviter le retry
          // storm qui saturait le pool de sockets Chrome
          // (ERR_INSUFFICIENT_RESOURCES).
          _maxRetries: 1,
          silent: true,
          // Vincent B1017 (Eric 2026-02-26) — Signal AbortController
          // partagé : coupe la requête si stopPolling ou unmount pendant
          // qu'elle est en vol.
          signal: abortRef.current?.signal,
        }
      );
      setPipelineStatus((prev) => {
        if (!prev) return status;
        // Nouvelle exécution (failed→processing ou complete→processing) : reset.
        if (prev.status !== status.status && status.status === "processing") {
          // Vincent B639 — reset le garde à chaque nouvelle relance.
          pausedTriggeredRef.current = false;
          return status;
        }
        // Clamp monotonic pour éviter les yoyos pendant `processing`.
        if (status.status === "processing" && status.progress < (prev.progress ?? 0)) {
          return { ...status, progress: prev.progress, stage: prev.stage };
        }
        return status;
      });
      // Vincent B639 (Eric 2026-07-31 SPEC STRICK PROD) — Trigger onComplete
      // sur transition vers un état paused_pending_*. Whisper + diarisation
      // viennent de finir et les segments sont insérés en DB — le frontend
      // doit recharger pour que l'onglet Transcriptions les affiche. On
      // garde le polling actif (le user peut reprendre le pipeline) mais
      // on ne rappelle onComplete qu'une seule fois par transition.
      if (
        _PAUSED_AFTER_TRANSCRIPTION.has(status.status)
        && !pausedTriggeredRef.current
        && typeof onCompleteRef.current === "function"
      ) {
        pausedTriggeredRef.current = true;
        onCompleteRef.current();
      }
      if (status.status === "complete" || status.status === "failed") {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (status.status === "complete" && typeof onCompleteRef.current === "function") {
          onCompleteRef.current();
        }
      }
    } catch {
      // silencieux — le polling reprend sur le prochain tick
    }
  }, [api, videoId]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pausedTriggeredRef.current = false; // Vincent B639 — reset au démarrage
    // Vincent B1017 — Nouvelle AbortController pour cette session de polling.
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    fetchPipelineStatus();
    // Vincent B1019 — L'intervalle effectif est adapté à la saturation
    // navigateur : 3s en normal, 15s pendant 30s après un throttle event.
    pollingRef.current = setInterval(
      fetchPipelineStatus,
      BASE_POLL_INTERVAL_MS * (throttleMultiplierRef.current || 1),
    );
  }, [fetchPipelineStatus]);

  // Vincent B1019 — Quand le multiplicateur change (throttle event puis
  // reset après 30s), on relance l'interval avec la nouvelle cadence
  // au lieu d'attendre le prochain unmount.
  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = setInterval(
        fetchPipelineStatus,
        BASE_POLL_INTERVAL_MS * (throttleMultiplier || 1),
      );
    }
  }, [throttleMultiplier, fetchPipelineStatus]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    // Vincent B1017 — Coupe les requêtes en vol.
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // Auto-cleanup au unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      // Vincent B1017 — Coupe les requêtes en vol au unmount.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  return { pipelineStatus, setPipelineStatus, startPolling, stopPolling };
}

export default usePipelineStatus;
